import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  IProjectsClient,
  ProjectHeatmapSettingDto,
  ProjectsClient,
  UpdateProjectHeatmapSettingsCommand
} from "app/ApiClient";
import { LoadingState } from "types";
import { initialProjectHeatmapSettingsState, ProjectHeatmapSettings } from "./projectHeatmapSettingsState";
import { isEmptyObject } from "utils/objectHelper";


const projectSettingsClient: IProjectsClient = new ProjectsClient(
  process.env.REACT_APP_API_URL,
);

type GetProjectHeatmapSettingsResponse = {
  projectSettings: ProjectHeatmapSettings;
} & ProjectHeatmapSettingDto;

export const getProjectHeatmapSettings = createAsyncThunk<GetProjectHeatmapSettingsResponse, number>(
  "projects/getProjectHeatmapSettings",
  async (projectId: number) => {
    const projectHeatmapSettings = await projectSettingsClient.getProjectHeatmapSettings(projectId);

    const normalizedConfiguration = (
      projectHeatmapSettings?.configuration ? JSON.parse(projectHeatmapSettings?.configuration) as unknown : {}
    ) as ProjectHeatmapSettings;

    return {
      ...projectHeatmapSettings,
      projectSettings: { ...normalizedConfiguration },
    };
  },
);

export const updateHeatmapSettings = createAsyncThunk<GetProjectHeatmapSettingsResponse, UpdateProjectHeatmapSettingsCommand>(
  "projects/updateProjectHeatmapSettings",
  async (updateProjectHeatmapSettingsCommand: UpdateProjectHeatmapSettingsCommand) => {
    const projectHeatmapSettings = await projectSettingsClient.updateProjectHeatmapSettings(updateProjectHeatmapSettingsCommand);
    const normalizedConfiguration = (
      projectHeatmapSettings?.configuration ? JSON.parse(projectHeatmapSettings?.configuration) as unknown : {}
    ) as ProjectHeatmapSettings;

    return {
      ...projectHeatmapSettings,
      projectSettings: { ...normalizedConfiguration },
    };
  },
);

const projectHeatmapSettingsSlice = createSlice({
  name: "projectHeatmapSettings",
  initialState: initialProjectHeatmapSettingsState,
  reducers: {
    resetProjectHeatmapSettings: () => initialProjectHeatmapSettingsState,
    resetProjectHeatmapSettingsLoadingState: (state) => {
      state.loading = LoadingState.idle;
    },
    setCurrentProjectSettings: (state, action: PayloadAction<Partial<ProjectHeatmapSettings>>) => {
      state.currentProjectSettings = {
        ...state.currentProjectSettings,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    // getProjectHeatmapSettings
    builder.addCase(getProjectHeatmapSettings.pending, (state) => {
      state.loading = LoadingState.pending;
    });
    builder.addCase(getProjectHeatmapSettings.fulfilled, (state, { payload }) => {
      state.currentProjectSettings = payload.projectSettings;
      state.loading = LoadingState.succeed;
    });
    builder.addCase(getProjectHeatmapSettings.rejected, (state) => {
      state.loading = LoadingState.failed;
    });
    // updateProjectHeatmapSettings
    builder.addCase(updateHeatmapSettings.pending, (state) => {
      state.loading = LoadingState.pending;
    });
    builder.addCase(updateHeatmapSettings.fulfilled, (state, { payload }) => {
      state.loading = LoadingState.succeed;
      if (isEmptyObject(state.currentProjectSettings)) {
        state.currentProjectSettings = {
          ...state.currentProjectSettings,
          ...payload.projectSettings,
        };
      } 
    });
    builder.addCase(updateHeatmapSettings.rejected, (state) => {
      state.loading = LoadingState.failed;
    });
  },
});

export const { resetProjectHeatmapSettings, resetProjectHeatmapSettingsLoadingState, setCurrentProjectSettings } = projectHeatmapSettingsSlice.actions;
export default projectHeatmapSettingsSlice.reducer;
